import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { IProviderProps, IZoomChangeContext } from "../@types/ContextProviderRelated";
import {
    DEFAULT_ZOOM_VALUE
} from "../utils/constants";
import { getZoomIntervals } from "../utils/numberUtils";
import { useSceneData } from "./SceneDataProvider";

const zoomChangeContextDefaultValues: IZoomChangeContext = {
    indicatorLabelFontSize: 0,
    setIndicatorLabelFontSize: () => {},
    indicatorLabelBoxPadding: 0,
    setIndicatorLabelBoxPadding: () => {},
    staggeredZoomData: DEFAULT_ZOOM_VALUE
};

export const ZoomChangeContext = createContext<IZoomChangeContext>(zoomChangeContextDefaultValues);

export const useZoomChangeContext = () => useContext(ZoomChangeContext);

const getStaggeredZoomValue = (zoom: number, intervals: number[]): number => {
    if (intervals.length === 0) {
        return zoom;
    }

    return intervals.find((interval) => zoom <= interval) ?? intervals[intervals.length - 1];
};

export const ZoomChangeProvider = ({ children }: IProviderProps): JSX.Element => {
    // Getting the rapidly updating zoomData value.
    const { zoomData } = useSceneData();
    // Zoom value debounced to interval.
    const [staggeredZoom, setStaggeredZoom] = useState<number>(zoomData);
    const zoomIntervals = useMemo(() => getZoomIntervals(), []);

    // UseEffect hook to react to changes in the zoomData within the predefined intervals.
    useEffect(() => {
        const nextStaggeredZoom = getStaggeredZoomValue(zoomData, zoomIntervals);
        setStaggeredZoom((current) => current === nextStaggeredZoom ? current : nextStaggeredZoom);
    }, [zoomData, zoomIntervals]);

    useEffect(() => {
        //console.log("staggeredZoom: " + staggeredZoom);
    }, [staggeredZoom]);

    // Size values that are dependent on the zoom changes.
    const [indicatorLabelFontSize, setIndicatorLabelFontSize] = useState<number>(zoomChangeContextDefaultValues.indicatorLabelFontSize);
    const [indicatorLabelBoxPadding, setIndicatorLabelBoxPadding] = useState<number>(zoomChangeContextDefaultValues.indicatorLabelBoxPadding);
    const value = useMemo<IZoomChangeContext>(() => ({
        indicatorLabelFontSize,
        setIndicatorLabelFontSize,
        indicatorLabelBoxPadding,
        setIndicatorLabelBoxPadding,
        staggeredZoomData: staggeredZoom
    }), [indicatorLabelFontSize, indicatorLabelBoxPadding, staggeredZoom]);

    return (
        <ZoomChangeContext.Provider value={value}>
            {children}
        </ZoomChangeContext.Provider>
    );
};
